/**
 * PaneLegendPrimitive — a one-line label pinned to the TOP-LEFT of an indicator pane:
 * the pane's name, then coloured value chips for the hovered bar (the latest bar when the
 * cursor is off the chart), e.g.  "PCR (OI)  ● 0.94  · 1.0 = balance".
 *
 * Canvas-drawn so it stays glued to its pane through resizes and pane drags. The text is
 * mutable (setItems) so a crosshair move only repaints, it never rebuilds the series.
 */
import type {
  ISeriesPrimitive,
  IPrimitivePaneView,
  IPrimitivePaneRenderer,
  SeriesAttachedParameter,
  Time,
} from 'lightweight-charts';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';

export interface PaneLegendItem { text: string; color?: string; bold?: boolean }

class Renderer implements IPrimitivePaneRenderer {
  constructor(private readonly p: PaneLegendPrimitive) {}
  draw(target: CanvasRenderingTarget2D): void {
    const { title, items, isDark } = this.p;
    if (!title && !items.length) return;
    target.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const px = Math.round(10.5 * Math.min(hpr, vpr));
      const font = (w: number) => `${w} ${px}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      const padX = 5 * hpr, padY = 2.5 * vpr, gap = 7 * hpr, dot = 3 * Math.min(hpr, vpr);
      const muted = isDark ? 'rgba(201,209,217,0.85)' : 'rgba(51,51,51,0.85)';
      const runs: Array<{ text: string; color: string; font: string; dot: boolean }> = [];
      if (title) runs.push({ text: title, color: muted, font: font(700), dot: false });
      for (const it of items) runs.push({ text: it.text, color: it.color || muted, font: font(it.bold ? 700 : 600), dot: !!it.color });
      ctx.save();
      let w = 0;
      for (const r of runs) { ctx.font = r.font; w += ctx.measureText(r.text).width + (r.dot ? dot * 2 + 3 * hpr : 0) + gap; }
      w = w - gap + padX * 2;
      const h = px + padY * 2;
      const x0 = (6 + this.p.leftInset) * hpr, y0 = 4 * vpr;
      ctx.fillStyle = isDark ? 'rgba(13,17,23,0.72)' : 'rgba(255,255,255,0.82)';
      const r = 4 * Math.min(hpr, vpr);
      ctx.beginPath();
      ctx.moveTo(x0 + r, y0); ctx.arcTo(x0 + w, y0, x0 + w, y0 + h, r); ctx.arcTo(x0 + w, y0 + h, x0, y0 + h, r);
      ctx.arcTo(x0, y0 + h, x0, y0, r); ctx.arcTo(x0, y0, x0 + w, y0, r); ctx.closePath(); ctx.fill();
      ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
      let x = x0 + padX;
      const yc = y0 + h / 2;
      for (const rn of runs) {
        if (rn.dot) {
          ctx.fillStyle = rn.color;
          ctx.beginPath(); ctx.arc(x + dot, yc, dot, 0, Math.PI * 2); ctx.fill();
          x += dot * 2 + 3 * hpr;
        }
        ctx.font = rn.font; ctx.fillStyle = rn.dot ? (isDark ? '#e6edf3' : '#1c2733') : rn.color;
        ctx.fillText(rn.text, x, yc);
        x += ctx.measureText(rn.text).width + gap;
      }
      ctx.restore();
    });
  }
}

class View implements IPrimitivePaneView {
  constructor(private readonly p: PaneLegendPrimitive) {}
  zOrder() { return 'top' as const; }
  renderer(): IPrimitivePaneRenderer { return new Renderer(this.p); }
}

export class PaneLegendPrimitive implements ISeriesPrimitive<Time> {
  title: string;
  items: PaneLegendItem[] = [];
  isDark: boolean;
  /** CSS px kept clear on the left (the OverView lane sits over the panes there). */
  leftInset = 0;
  private requestUpdate: (() => void) | null = null;
  private readonly views: View[];
  constructor(title: string, isDark: boolean) { this.title = title; this.isDark = isDark; this.views = [new View(this)]; }
  attached(p: SeriesAttachedParameter<Time>): void { this.requestUpdate = p.requestUpdate; }
  detached(): void { this.requestUpdate = null; }
  updateAllViews(): void { /* reads fields at draw time */ }
  paneViews(): readonly IPrimitivePaneView[] { return this.views; }
  setItems(items: PaneLegendItem[]): void {
    const same = items.length === this.items.length && items.every((it, i) => it.text === this.items[i].text && it.color === this.items[i].color);
    if (same) return;
    this.items = items;
    this.requestUpdate?.();
  }
}
